import React, { ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { User } from '../../types/api';
import { clearCurrent } from '../Wizard/wizardState';
import './Layout.css';

interface Tab { to: string; label: string; end?: boolean; highlight?: boolean; staffOnly?: boolean; }

const TABS: Tab[] = [
  { to: '/start',     label: 'Start a post', highlight: true },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/calendar',  label: 'Calendar' },
  { to: '/create',    label: 'Create Post' },
  { to: '/editor',    label: 'Video Editor' },
  { to: '/audio',     label: 'Audio Library' },
  { to: '/accounts',  label: 'Accounts' },
  { to: '/hashtags',  label: 'Hashtags' },
  { to: '/admin',     label: 'Admin', staffOnly: true },
];

interface FloatingMenuToggleProps {
  onClick: () => void;
}

/* Top-right floating menu button. Always visible — sits above the
 * scrollable content area at fixed viewport coords so the user can
 * pop the nav from any scroll position without the header chrome
 * having to stick. */
export const FloatingMenuToggle: React.FC<FloatingMenuToggleProps> = ({ onClick }) => (
  <button
    type="button"
    className="floating-menu-toggle"
    onClick={onClick}
    aria-label="Open menu"
    aria-haspopup="menu"
  >
    <span className="hamburger" aria-hidden>
      <span /><span /><span />
    </span>
  </button>
);

interface NavSheetProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
  user: User | null;
}

/* Slide-in nav sheet hosted at the layout level so it overlays every
 * page. Closes on backdrop click, the X button, Escape, or selecting
 * any nav item. Sign out lives at the bottom, separated by a divider,
 * so destructive actions don't sit next to navigation. */
export const NavSheet: React.FC<NavSheetProps> = ({ open, onClose, onLogout, user }) => {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <div
      className={`nav-sheet${open ? ' is-open' : ''}`}
      aria-hidden={!open}
    >
      <div
        className="nav-sheet-backdrop"
        onClick={onClose}
        role="presentation"
      />
      <aside
        className="nav-sheet-panel"
        role="menu"
        aria-label="Site navigation"
      >
        <div className="nav-sheet-head">
          <span className="nav-sheet-title">Menu</span>
          <button
            type="button"
            className="ghost btn--icon-sm"
            onClick={onClose}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <nav className="nav-sheet-list">
          {TABS.filter((tab) => !tab.staffOnly || user?.is_staff).map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              onClick={onClose}
              className={({ isActive }) => [
                'nav-button',
                isActive ? 'active' : '',
                tab.highlight ? 'nav-button--cta' : '',
              ].filter(Boolean).join(' ')}
            >
              {tab.highlight && <span className="nav-button-mark" aria-hidden>+</span>}
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <div className="nav-sheet-foot">
          <button
            type="button"
            className="danger nav-sheet-signout"
            onClick={() => { onClose(); onLogout(); }}
          >
            Sign out
          </button>
          {/* Build stamp — REACT_APP_VERSION is the git short SHA baked
            * in by Dockerfile.prod; 'dev' on the local dev server. Lets
            * the user confirm the browser isn't serving a stale bundle. */}
          <div className="nav-sheet-version muted">
            build {process.env.REACT_APP_VERSION || 'dev'}
          </div>
        </div>
      </aside>
    </div>
  );
};

interface MainLayoutProps {
  children: ReactNode;
  /* Retained in the signature for API compatibility; the layout no
   * longer renders a username chip in the top bar. */
  user: User | null;
  onLogout: () => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, user, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="main-layout">
      <main className="main-content">
        <div className="main-content-inner">
          <div className="logo-banner">
            <img
              src="/megaphony-logo.png"
              alt="Megaphony"
              className="logo-banner-img"
            />
          </div>
          {children}
        </div>
      </main>
      {/* Gradient underlay so content scrolling beneath the floating
        * controls fades into dark navy instead of colliding visually. */}
      <div className="header-underlay" aria-hidden />
      <div className="floating-controls">
        <button
          type="button"
          className="floating-btn"
          onClick={() => navigate('/dashboard')}
          aria-label="Dashboard"
          title="Dashboard"
        >
          ⌂
        </button>
        <button
          type="button"
          className="floating-btn floating-btn--cta"
          onClick={() => { clearCurrent(); navigate('/start'); }}
          aria-label="New post"
          title="New post"
        >
          +
        </button>
        <FloatingMenuToggle onClick={() => setMenuOpen(true)} />
      </div>
      <NavSheet open={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} user={user} />
    </div>
  );
};
